import { useEffect, useRef, useState } from 'react';
import styles from './PatientElasticSearchDropdown.module.scss';
import { Flex, Spin } from 'antd';
import toast from 'react-hot-toast';
import { getErrorString, isEmptyObject } from '../../../utils';
import { filterFieldFormat } from '../../../utils/filters-util';
import { searchPatientsApi } from '../../../api/patients-api';
import { Avatar } from '../../../common';
import { useDispatch, useSelector } from 'react-redux';
import { selectPatients } from '../../../reducers/selectors';
import { resetList, setList, setListLoaders } from '../../../reducers/patients-slice';
import Unavailable from '../../../common/unavailable';
import { useLocation } from 'react-router-dom';

const PatientElasticSearchDropdown = ({
  show = false,
  inputRef,
  handleBlur = () => {},
  item,
  filtersObject,
  onSearch,
  isCustomUserSearch = false,
  fetchListingFunc = searchPatientsApi,
  queryKey = 'patient_info',
  hideDropdown = false,
}) => {
  const dispatch = useDispatch();
  const containerRef = useRef();
  const { pathname } = useLocation();
  const params = new URLSearchParams(window.location.search);
  const itemKey = params.get(item?.key);
  const value =
    filterFieldFormat(filtersObject[item.key || '']) === undefined
      ? itemKey
      : filterFieldFormat(filtersObject[item.key || '']);
  const { list: listObject, listLoaders } = useSelector(selectPatients);
  const isLoading = listLoaders?.[value];
  const list = listObject?.[value];
  const allSearchQueries = Object.keys(listObject || {});
  const elasticKey = item?.elasticKey || 'f[lead.id][in]';
  const usePatientId = item?.usePatientId;
  const ids = (list || [])?.map((d) =>
    (usePatientId ? d?.lead?.patient?.id : isCustomUserSearch ? d?.id : d?.lead?.id)?.toString(),
  );
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef([]);

  useEffect(() => setHighlightedIndex(-1), [show]);

  useEffect(() => {
    if (!value) {
      dispatch(resetList());
    }
  }, [pathname]);

  const fetchSearchPatients = async () => {
    if (!value && !isEmptyObject(filtersObject)) {
      dispatch(setList({ key: '', value: [] }));
      let obj = {
        ...filtersObject,
        [item?.key]: value,
      };
      delete obj[elasticKey];
      onSearch(obj, true);
      return;
    }
    if (!value) return;
    if (allSearchQueries?.includes(value)) return;
    try {
      dispatch(setListLoaders({ key: value, value: true }));
      const payload = { [queryKey]: value?.replace(/^\s+/, '') };
      const response = await fetchListingFunc({ ...payload, per_page: 20 });
      if (response?.status) {
        dispatch(setList({ key: value, value: response?.data?.table?.records || [] }));
      }
    } catch (error) {
      toast.error(getErrorString(error));
    } finally {
      dispatch(setListLoaders({ key: value, value: false }));
    }
  };

  useEffect(() => {
    fetchSearchPatients();
  }, [filtersObject[item.key || '']]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      setTimeout(() => {
        if (
          inputRef.current &&
          !inputRef.current.contains(event.target) &&
          containerRef.current &&
          !containerRef.current.contains(event.target)
        ) {
          handleBlur();
        }
      }, 0);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionClick = (i) => {
    let obj = {
      ...filtersObject,
      [item?.key]: (isCustomUserSearch ? i?.name : i?.lead?.patient?.full_name) || i?.email?.split('@')?.[0],
      [elasticKey]: `${usePatientId ? i?.lead?.patient?.id : isCustomUserSearch ? i?.id : i?.lead?.id}`,
    };
    delete obj?.page;
    onSearch(obj, true);
    handleBlur();
  };

  const handleSelectAll = () => {
    const obj = {
      ...filtersObject,
      [elasticKey]: `${ids?.join(',')}`,
    };
    delete obj?.select;
    delete obj?.page;
    onSearch(obj, true);
    handleBlur();
  };

  const handleKeyDown = (e) => {
    if (!show || !list?.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        if (prev === -1) return 0;
        if (prev === list.length - 1) return -1;
        return prev + 1;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        if (prev === -1) return list.length - 1;
        if (prev === 0) return -1;
        return prev - 1;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0) {
        handleOptionClick(list[highlightedIndex]);
      } else {
        handleSelectAll();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [highlightedIndex, list, show]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current[highlightedIndex]) {
      listRef.current[highlightedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [highlightedIndex]);

  if (((list || [])?.length === 0 && !value) || hideDropdown) return null;

  return (
    <Flex ref={containerRef} gap={3} vertical className={`${styles.customOptionContainer} ${show && styles.show}`}>
      {isLoading ? (
        <Flex justify="center" align="center" className="my-5">
          <Spin />
        </Flex>
      ) : (list || [])?.length === 0 ? (
        <div className="mt-4">
          <Unavailable customDesc={'No Contact Found'} customGap={10} />
        </div>
      ) : (
        list?.map((item, index) => {
          const isHighlighted = index === highlightedIndex;
          const name = isCustomUserSearch ? item?.name : item?.lead?.patient?.full_name;
          return (
            <Flex
              key={usePatientId ? item?.lead?.item?.id : item?.lead?.id}
              ref={(el) => (listRef.current[index] = el)}
              onClick={() => handleOptionClick(item)}
              className={`${styles.hoverBg} ${isHighlighted && styles.highligted}`}
              gap={6}
              justify="start"
              align="center"
            >
              <Flex align="center" justify="space" gap={5}>
                <Avatar size={35} src={item?.profile_picture} isSystem={false} name={name} />
                <Flex vertical gap={2}>
                  <div className="body-12-medium">{name}</div>
                  <div className="text-2">{item?.email}</div>
                </Flex>
              </Flex>
            </Flex>
          );
        })
      )}
    </Flex>
  );
};

export default PatientElasticSearchDropdown;
